import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  StandardizedTable,
  type TableRow,
} from "@/components/ui/standardized-table";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { useAccount } from "@/contexts/account.context";
import { formatDateBR } from "@/utils/dateFormat";
import { formatCurrency, formatInteger } from "@/utils/numberFormat";
import { apiV1AcquisitionOrdersGet } from "@/backend-sdk/client/sdk.gen";
import type { AcquisitionOrderItem as GeneratedAcquisitionOrderItem } from "@/backend-sdk/client/types.gen";
import type { TableQueryDTO } from "@/types/table-query";

/**
 * ANÁLISE DE PEDIDOS — a página que o item 28 da auditoria da Magnata registrou como
 * ausente.
 *
 * A auditoria avisa que "página ausente" merece ser conferida em três lugares antes de
 * virar trabalho — rota, tela e menu —, porque o item 25 (Análise Mensal por Canal) acabou
 * sendo só o terceiro: rota e tela existiam, e faltava caminho até elas. Aqui faltavam os
 * três. `GET /api/v1/acquisition/orders` é rota nova, esta é a tela, e a entrada no menu
 * lateral fica ao lado de Aquisição.
 *
 * ## O que ela responde
 *
 * As outras tabelas do painel respondem "quanto cada canal vendeu". Esta responde a
 * pergunta inversa, e é a única que responde: **um pedido por linha**, com as seis
 * dimensões que dizem de onde ele veio — canal, origem/mídia, campanha, dispositivo,
 * estado e cidade. É o que permite olhar um pedido de R$ 1.598,00 e saber qual anúncio o
 * trouxe.
 *
 * ## O rodapé vem da rota
 *
 * ⚠️ **`total` é passado, e não montado aqui.** Quando o `StandardizedTable` não recebe um
 * rodapé, ele monta o dele somando as linhas **renderizadas** — o par de defeitos A+B que a
 * auditoria encontrou em sete tabelas diferentes. Numa tabela paginada de pedidos isso
 * mostraria a receita de vinte e cinco pedidos sob o rótulo "Total Geral". A rota publica
 * `total_row` sobre o conjunto inteiro, já filtrado e antes de paginar.
 */

type OrderRow = GeneratedAcquisitionOrderItem & TableRow;

const PAGE_SIZE = 25;

const AnalisePedidosPage: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const { selectedAccount } = useAccount();

  const startDate = useMemo(
    () => formatDateToISO(filterState.dateRange.startDate),
    [filterState.dateRange.startDate],
  );
  const endDate = useMemo(
    () => formatDateToISO(filterState.dateRange.endDate),
    [filterState.dateRange.endDate],
  );

  const resolveCustomerUuid = useCallback(
    () =>
      selectedAccount?.customer_uuid ||
      localStorage.getItem("active_customer_uuid") ||
      "",
    [selectedAccount],
  );

  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useState<TableQueryDTO>({});
  // Nasce carregando: com `false`, a primeira pintura mostraria "Nenhum dado encontrado"
  // antes de o efeito disparar a busca.
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [totalRow, setTotalRow] = useState<OrderRow | null>(null);
  const [total, setTotal] = useState<number>(0);

  // O período novo pode ter menos páginas que o anterior; ficar na página 7 de uma tabela
  // de duas devolveria a lista vazia sem erro.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1);
  }, [startDate, endDate, selectedAccount]);

  useEffect(() => {
    const carregar = async () => {
      const customerUuid = resolveCustomerUuid();
      if (!customerUuid) {
        setLoading(false);
        setRows([]);
        setTotalRow(null);
        setTotal(0);
        return;
      }

      setLoading(true);
      try {
        const { data } = await apiV1AcquisitionOrdersGet({
          query: {
            customer_uuid: customerUuid,
            start_date: startDate,
            end_date: endDate,
            page,
            page_size: PAGE_SIZE,
            q: query.q,
            sort_by: query.sortBy,
            sort_dir: query.sortDir,
            filters: query.filters?.length
              ? JSON.stringify(query.filters)
              : undefined,
          },
          throwOnError: true,
        });
        setRows((data?.items ?? []).map((item) => ({ ...item }) as OrderRow));
        setTotalRow((data?.total_row ?? null) as OrderRow | null);
        setTotal(data?.pagination?.total ?? 0);
      } catch (err) {
        console.error("Erro ao carregar a análise de pedidos:", err);
        setRows([]);
        setTotalRow(null);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [startDate, endDate, page, query, resolveCustomerUuid]);

  return (
    <div className="p-6 space-y-6 page-background">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="page-title">ANÁLISE DE PEDIDOS</h1>
      </div>

      <GlobalFilter />

      {/* ⚠️ A tabela é desenhada **sempre**, como as nove de `AcquisitionPage`, e quem mostra
          o estado vazio é o próprio `StandardizedTable`. Trocá-la por um card quando
          `total === 0` prenderia a tela: `total` é o `pagination.total` da rota, que já
          reflete a busca (`filtrados.length`), então um termo sem resultado desmontaria a
          tabela levando junto o campo de busca — e o `q` ficaria no estado da página, sem
          nada que o pudesse limpar. */}
      <StandardizedTable
        title="PEDIDOS DO PERÍODO"
        description="Um pedido por linha, com a origem da sessão que o gerou"
        loading={loading}
        columns={[
          {
            key: "transaction_id",
            header: "Pedido",
            align: "left",
            type: "text",
          },
          {
            key: "date",
            header: "Data",
            align: "left",
            type: "date",
            // Duas linhas não trazem data `YYYY-MM-DD`: o rodapé, que a publica vazia
            // (não há "data do total"), e o pedido cuja dimensão o GA4 não devolveu, que
            // vem como `N/D` — a mesma reserva das outras sete colunas. `formatDateBR` de
            // qualquer uma das duas devolveria lixo, então elas saem como estão.
            render: (value) => {
              const bruto = String(value ?? "");
              return /^\d{4}-\d{2}-\d{2}$/.test(bruto)
                ? formatDateBR(bruto)
                : bruto;
            },
          },
          {
            key: "channel_group",
            header: "Canal",
            align: "left",
            type: "text",
          },
          {
            key: "source_medium",
            header: "Origem / Mídia",
            align: "left",
            type: "text",
          },
          {
            key: "campaign",
            header: "Campanha",
            align: "left",
            type: "text",
          },
          {
            key: "device",
            header: "Dispositivo",
            align: "left",
            type: "text",
          },
          { key: "region", header: "Estado", align: "left", type: "text" },
          { key: "city", header: "Cidade", align: "left", type: "text" },
          {
            key: "items",
            header: "Itens",
            align: "right",
            type: "number",
            render: (value) => formatInteger(Number(value ?? 0)),
          },
          {
            key: "revenue",
            header: "Receita",
            align: "right",
            type: "currency",
            render: (value) => formatCurrency(Number(value ?? 0)),
          },
        ]}
        data={rows}
        total={totalRow ?? undefined}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: setPage,
        }}
        queryMode="server"
        onQueryChange={(q) => {
          setQuery(q);
          setPage(1);
        }}
      />
    </div>
  );
};

export default AnalisePedidosPage;
